/**
 * Mise en forme des dates, heures et nombres, dans la langue du membre.
 * Toutes les chaines visibles par l'utilisateur passent par ici, ce qui
 * evite d'eparpiller des `toLocaleString` dans les ecrans.
 */
import { locale, t } from '../i18n';

/**
 * L'heure courante.
 *
 * Les donnees venaient autrefois d'un jeu de demonstration fige en aout
 * 2026, ce qui obligeait a decaler l'horloge pour que "il y a 4 min"
 * garde un sens. Elles sont maintenant datees par la base, en temps
 * reel : la vraie heure est la bonne reference.
 */
function now(): number {
  return Date.now();
}

/**
 * Duree ecoulee, format court du feed : "il y a 4 min", "il y a 2 h",
 * "il y a 3 j", "il y a 1 sem.".
 */
export function timeAgo(iso: string): string {
  const elapsed = now() - Date.parse(iso);
  const minutes = Math.floor(elapsed / 60_000);

  if (minutes < 1) return t.temps.ilYa.instant;
  if (minutes < 60) return t.temps.ilYa.minutes(minutes);

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t.temps.ilYa.heures(hours);

  const days = Math.floor(hours / 24);
  if (days < 7) return t.temps.ilYa.jours(days);

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return t.temps.ilYa.semaines(weeks);

  const months = Math.floor(days / 30);
  if (months < 12) return t.temps.ilYa.mois(months);

  return t.temps.ilYa.ans(Math.floor(days / 365));
}

/**
 * Horodatage d'une conversation : l'heure si c'est aujourd'hui,
 * "hier", puis la date courte au-dela.
 */
export function conversationTime(iso: string): string {
  const date = new Date(iso);
  const today = new Date(now());
  const isSameDay = date.toDateString() === today.toDateString();
  if (isSameDay) return timeOfDay(iso);

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return t.temps.hier;

  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

/** "12:40". */
export function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/** Separateur de journee dans une discussion : "Aujourd'hui", "Hier", date longue. */
export function dayLabel(iso: string): string {
  const date = new Date(iso);
  const today = new Date(now());
  if (date.toDateString() === today.toDateString()) return t.temps.aujourdhui;

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return t.temps.Hier;

  return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
}

/** "16/08/26", le format des cartes d'evenement. */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

/**
 * L'heure et le jour, en toutes lettres chiffrees : "23:18 · 05/09/2026".
 *
 * Le fil d'une reponse l'ecrit sous elle. Partout ailleurs on donne l'age
 * — « il y a 2 h » se lit sans calculer — mais sous la reponse qu'on a
 * ouverte, l'age ne suffit plus : c'est la qu'on veut savoir quand la
 * chose a ete dite, et « il y a 3 j » ne repond pas a cette question.
 */
export function horodatage(iso: string): string {
  const date = new Date(iso);
  const heure = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const jour = date.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `${heure} · ${jour}`;
}

/** "26/06", pour les listes compactes. */
export function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

/** Pastille de date des cartes d'agenda : { month: "AOÛT", day: "16" }. */
export function dateBadge(iso: string): { month: string; day: string } {
  const date = new Date(iso);
  return {
    month: date.toLocaleDateString(locale, { month: 'short' }).replace('.', '').toUpperCase(),
    day: String(date.getDate()),
  };
}

/** Nombre seul, avec separateur de milliers. */
export function number(value: number): string {
  return value.toLocaleString(locale).replace(/ /g, ' ');
}

/**
 * Un horaire dans la langue du membre : "17h", "17h30" en francais,
 * "17:00", "17:30" en anglais.
 *
 * Le formulaire de creation rend desormais des heures natives ("17:00"),
 * alors que les evenements d'origine portent deja "17h". Les deux formes
 * coexistent donc dans la base, et c'est ici qu'elles se rejoignent.
 */
export function formatTime(value: string): string {
  const time = (value ?? '').trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return time;
  const minutes = match[2];
  return minutes === '00'
    ? t.temps.heure.pleine(Number(match[1]))
    : t.temps.heure.avecMinutes(Number(match[1]), minutes);
}

/** "17h – 18h", avec le tiret demi-cadratin des maquettes. */
export function timeRange(start: string, end: string): string {
  /*
    Des espaces fines de part et d'autre du tiret.
    Avec des espaces pleines, le tiret paraissait colle a l'heure de fin :
    son dessin ne remplit pas sa chasse de la meme facon des deux cotes,
    et l'ecart se voyait. Reduire l'espace rend le defaut imperceptible et
    serre la paire, qui se lit comme une seule valeur.
  */
  return `${formatTime(start)}\u2009–\u2009${formatTime(end)}`;
}

/**
 * L'inverse : "17h30" -> "17:30", la valeur qu'attend un `input[type=time]`.
 * Rend une chaine vide si l'horaire stocke n'est pas exploitable, pour ne
 * pas remplir le champ avec une valeur que le navigateur refusera.
 */
export function toTimeInput(value: string): string {
  const time = (value ?? '').trim();
  const match = /^(\d{1,2})\s*[h:]\s*(\d{2})?/.exec(time);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2] ?? '00'}`;
}

/**
 * Aujourd'hui au format "AAAA-MM-JJ", celui d'un `input[type=date]`.
 * Construit champ par champ, dans le fuseau du navigateur : passer par
 * `toISOString()` reculerait d'un jour a l'ouest de Greenwich.
 */
export function todayIso(): string {
  const date = new Date(now());
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Vrai si la date tombe dans les sept prochains jours. */
export function isThisWeek(iso: string): boolean {
  const delta = Date.parse(iso) - now();
  return delta >= 0 && delta < 7 * 24 * 60 * 60 * 1000;
}

/** Convertit "JJ / MM / AAAA" saisi au clavier en "AAAA-MM-JJ". */
export function parseFrenchDate(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  const date = new Date(`${year}-${month}-${day}`);
  if (Number.isNaN(date.getTime())) return null;
  return `${year}-${month}-${day}`;
}

/** Applique le masque JJ / MM / AAAA pendant la saisie. */
export function maskFrenchDate(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
  return parts.join(' / ');
}

/**
 * Colle la ponctuation double au mot qui la precede.
 *
 * Le francais veut une espace avant `?`, `!`, `:` et `;` — mais une
 * espace insecable, sinon la ligne casse juste devant et le signe se
 * retrouve seul en bas de la fenetre. C'est exactement ce qui arrivait
 * aux questions de confirmation, dont la derniere ligne ne portait qu'un
 * point d'interrogation.
 */
export function tightPunctuation(text: string): string {
  return text.replace(/ ([?!:;])/g, '\u00a0$1');
}

/**
 * Le contenu d'une pastille de compte.
 *
 * Au-dela de neuf, le nombre exact n'apprend plus rien et il faudrait
 * elargir la pastille a chaque dizaine : « 9+ » dit la meme chose et tient
 * dans un rond.
 */
export function compte(nombre: number): string {
  return nombre > 9 ? '9+' : String(nombre);
}
